/**
 * Chat Product Creation
 *
 * Shared parser and prompt builders for Chat-native Super Agent mentions.
 *
 * Primary responsibilities:
 * - Parse `@Sites` and `@Engines` creation mentions
 * - Build draft prompts and intake payloads for canvas creation surfaces
 *
 * Dependencies:
 * - Losi Sites experience metadata
 */

import {
  DEFAULT_LOSI_SITE_EXPERIENCE,
  LOSI_SITE_EXPERIENCES,
  type LosiSiteExperience,
  type LosiSiteExperienceId
} from '@/lib/sites/site-experiences'

export type ChatProductCreationKind = 'sites' | 'engines'

export interface ChatProductCreationCommand {
  kind: ChatProductCreationKind
  prompt: string
  raw: string
  siteExperienceId?: LosiSiteExperienceId
}

export type ChatProductCreationStatus = 'queued' | 'running' | 'completed' | 'error'

export interface ChatProductCreationToolResult {
  canvas?: {
    mode?: string
    summary?: string
    title?: string
  }
  created?: 'site' | 'engine_manifest' | string
  dashboard?: unknown
  engine?: {
    href?: string
    id?: string
    name?: string
    status?: string
  }
  error?: string
  helpers?: unknown
  nextActions?: string[]
  runtime?: {
    reason?: string
    started?: boolean
  }
  site?: {
    href?: string
    id?: string
    name?: string
    slug?: string
    status?: string
    type?: string
  }
  success?: boolean
  superAgent?: 'Sites' | 'Engines' | string
}

export interface ActiveChatProductCreation extends ChatProductCreationCommand {
  error?: string | null
  id: string
  result?: ChatProductCreationToolResult | null
  status?: ChatProductCreationStatus
  toolName?: string | null
  toolRunId?: string | null
  createdAt: number
  updatedAt?: number
}

export interface EngineCreationIntake {
  constraints: string[]
  domain: string
  integrations: string[]
  knowledgeSources: string[]
  name: string
  outcomes: string[]
  prompt: string
  summary: string
  targetUsers: string[]
  tools: string[]
  uiSurfaces: string[]
}

export interface ProductCreationHelper {
  id: string
  name: string
  focus: string
  responsibilities: readonly string[]
  outputs: readonly string[]
}

export interface SiteCreationStructure {
  version: 1
  siteType: LosiSiteExperienceId
  theme: {
    accentColor: string
    backgroundColor: string
    textColor: string
  }
  pages: Array<{
    path: string
    title: string
    description: string
    blocks: Array<{
      id: string
      type: 'hero' | 'section' | 'feature-list' | 'cta' | 'form-placeholder'
      eyebrow?: string
      title: string
      body?: string
      buttonHref?: string
      buttonLabel?: string
      fields?: Array<{
        id: string
        kind: 'email' | 'number' | 'select' | 'text' | 'textarea'
        label: string
        required: boolean
      }>
      items?: string[]
    }>
  }>
}

export interface SiteCreationSettings {
  source: 'chat-super-agent'
  superAgent: 'Sites'
  brief: string
  helperLanes: Array<{
    id: string
    name: string
    focus: string
    outputs: readonly string[]
  }>
}

const STRUCTURED_SUPER_AGENT_MATCH =
  /^@\[[^\]]+\]\(super-agent:(sites|site|engines|engine)\)(?:(?:\s+|[/:]\s*|-+\s*)([\s\S]*))?$/i
const DIRECT_SUPER_AGENT_MATCH = /^@([a-z]+)(?:(?:[/:]\s*|\s+|-+\s*)([\s\S]*))?$/i
const SITE_COMMANDS = new Set(['site', 'sites'])
const ENGINE_COMMANDS = new Set(['engine', 'engines'])

const SITE_EXPERIENCE_TERMS: ReadonlyArray<{
  id: LosiSiteExperienceId
  terms: readonly string[]
}> = [
  { id: 'interactive-page', terms: ['interactive page', 'interactive'] },
  { id: 'landing-page', terms: ['landing page', 'landing'] },
  { id: 'mini-app', terms: ['mini app', 'mini-app', 'miniapp'] },
  { id: 'calculator', terms: ['calculator', 'calc', 'estimate tool', 'roi tool'] },
  { id: 'portal', terms: ['portal', 'client portal', 'customer portal'] },
  { id: 'quiz', terms: ['quiz', 'assessment', 'scoring quiz'] },
  { id: 'form', terms: ['form', 'intake', 'application', 'lead capture'] },
  { id: 'website', terms: ['website', 'site', 'web site'] }
]

const BASE_SITE_HELPERS: readonly ProductCreationHelper[] = [
  {
    id: 'site-strategy',
    name: 'Strategy Specialist',
    focus: 'Audience, offer, goals, and conversion plan',
    responsibilities: [
      'Clarify the target audience and job to be done',
      'Set the page or product goal',
      'Choose the right Sites experience type'
    ],
    outputs: ['Creative brief', 'Conversion goal', 'Success criteria']
  },
  {
    id: 'site-ux',
    name: 'UX Specialist',
    focus: 'Information architecture, screens, sections, and states',
    responsibilities: [
      'Map the screen or page flow',
      'Design default sections and interaction states',
      'Identify mobile-first layout needs'
    ],
    outputs: ['Sitemap or flow', 'Section plan', 'State map']
  },
  {
    id: 'site-content',
    name: 'Content Specialist',
    focus: 'Copy, messaging, assets, and brand voice',
    responsibilities: [
      'Draft core messaging and content blocks',
      'Identify images, proof, and examples needed',
      'Keep tone aligned with workspace knowledge'
    ],
    outputs: ['Copy outline', 'Asset list', 'Content gaps']
  },
  {
    id: 'site-data',
    name: 'Data Specialist',
    focus: 'Forms, calculators, quizzes, portals, and structured inputs',
    responsibilities: [
      'Define fields, controls, formulas, or scoring',
      'Set validation and permission needs',
      'Plan submission or result storage'
    ],
    outputs: ['Field/control model', 'Validation rules', 'Data routing']
  },
  {
    id: 'site-publishing',
    name: 'Publishing and SEO Specialist',
    focus: 'Preview, SEO, domains, analytics, and launch readiness',
    responsibilities: [
      'Prepare preview-to-publish requirements',
      'Map SEO, metadata, and custom domain needs',
      'Define analytics events and conversion tracking'
    ],
    outputs: ['Publish checklist', 'SEO metadata', 'Analytics plan']
  },
  {
    id: 'site-review',
    name: 'QA and Analytics Specialist',
    focus: 'QA, accessibility, screenshot critique, and improvement loop',
    responsibilities: [
      'Check completeness and edge cases',
      'Review accessibility and responsive behavior',
      'Create critique/tweak requests for the next pass'
    ],
    outputs: ['QA checklist', 'Accessibility notes', 'Tweak list']
  }
]

const ENGINE_HELPERS: readonly ProductCreationHelper[] = [
  {
    id: 'engine-architecture',
    name: 'Architecture Specialist',
    focus: 'Engine purpose, boundaries, entities, and lifecycle',
    responsibilities: [
      'Check whether the repeated workflow qualifies as an Engine',
      'Turn the brief into an Engine manifest and dashboard blueprint',
      'Define entities, processes, statuses, and lifecycle stages',
      'Keep generated Engines internal until approved'
    ],
    outputs: ['Qualification check', 'Dashboard blueprint', 'Lifecycle map']
  },
  {
    id: 'engine-runtime',
    name: 'Runtime Specialist',
    focus: 'Safe execution, tool routing, and Super Agent orchestration',
    responsibilities: [
      'Plan review-safe runtime execution',
      'Route only allowlisted tools',
      'Block write actions until approval exists'
    ],
    outputs: ['Runtime plan', 'Tool route map', 'Safety gates']
  },
  {
    id: 'engine-data',
    name: 'Data Specialist',
    focus: 'Permissions, storage, memory, and knowledge sources',
    responsibilities: [
      'Identify data scopes and sensitivity',
      'Map Workspace Knowledge dependencies',
      'Define retention and audit requirements'
    ],
    outputs: ['Data scope map', 'Permission model', 'Audit notes']
  },
  {
    id: 'engine-ui',
    name: 'UI Specialist',
    focus: 'Custom dashboard tabs, sections, workflow canvases, and controls',
    responsibilities: [
      'Design the Engine-specific dashboard UI',
      'Define tabs, sections, records, controls, and workflow canvases',
      'Map progress, logs, approval, and settings views'
    ],
    outputs: ['Dashboard tabs', 'Section map', 'Workflow UI plan']
  },
  {
    id: 'engine-observability',
    name: 'Observability Specialist',
    focus: 'Runs, costs, logs, errors, metrics, and evaluation',
    responsibilities: [
      'Track run events and errors',
      'Plan cost and performance summaries',
      'Define evaluations before activation'
    ],
    outputs: ['Run log plan', 'Cost summary', 'Evaluation checklist']
  },
  {
    id: 'engine-approval',
    name: 'Approval Specialist',
    focus: 'Human review, approvals, rollout, and activation controls',
    responsibilities: [
      'Create approval checklists',
      'Enforce human review before activation',
      'Prepare rollout state changes'
    ],
    outputs: ['Approval checklist', 'Review package', 'Rollout gates']
  }
]

// --------------

function normalizeForMatch(value: string) {
  return value
    .toLowerCase()
    .replace(/[-_]+/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function commandToKind(value: string): ChatProductCreationKind | null {
  const command = value.trim().toLowerCase()

  if (SITE_COMMANDS.has(command)) return 'sites'
  if (ENGINE_COMMANDS.has(command)) return 'engines'

  return null
}

function getEngineNameFromPrompt(prompt: string) {
  const normalizedPrompt = prompt
    .replace(/^create\s+(an?\s+)?/i, '')
    .replace(/^build\s+(an?\s+)?/i, '')
    .replace(/^make\s+(an?\s+)?/i, '')
    .replace(/^generate\s+(an?\s+)?/i, '')
    .replace(/\s+/g, ' ')
    .trim()

  if (!normalizedPrompt) {
    return 'Content Engine'
  }

  const words = normalizedPrompt.split(' ').slice(0, 7).join(' ')
  const title = words.replace(/\b\w/g, (character) => character.toUpperCase())

  return /engine$/i.test(title) ? title : `${title} Engine`
}

function listToLines(values: readonly string[]) {
  return values.map((value) => `- ${value}`).join('\n')
}

function helpersToPromptLines(helpers: readonly ProductCreationHelper[]) {
  return helpers
    .map((helper) => {
      const responsibilities = helper.responsibilities
        .map((responsibility) => `  - ${responsibility}`)
        .join('\n')

      return `${helper.name}: ${helper.focus}\n${responsibilities}`
    })
    .join('\n\n')
}

// --------------

export function resolveSiteExperienceIdFromPrompt(
  prompt: string
): LosiSiteExperienceId {
  const normalizedPrompt = normalizeForMatch(prompt)
  if (!normalizedPrompt) return DEFAULT_LOSI_SITE_EXPERIENCE.id

  const haystack = ` ${normalizedPrompt} `
  const match = SITE_EXPERIENCE_TERMS.find((experience) =>
    experience.terms.some((term) => {
      const normalizedTerm = normalizeForMatch(term)
      return haystack.includes(` ${normalizedTerm} `)
    })
  )

  return match?.id ?? DEFAULT_LOSI_SITE_EXPERIENCE.id
}

export function parseChatProductCreationCommand(
  input: string
): ChatProductCreationCommand | null {
  const raw = input.trim()
  const match = raw.match(STRUCTURED_SUPER_AGENT_MATCH) ?? raw.match(DIRECT_SUPER_AGENT_MATCH)

  if (!match) return null

  const kind = commandToKind(match[1])
  if (!kind) return null

  const prompt = (match[2] ?? '').trim()

  return {
    kind,
    prompt,
    raw,
    ...(kind === 'sites'
      ? { siteExperienceId: resolveSiteExperienceIdFromPrompt(prompt) }
      : {})
  }
}

export function isChatProductCreationMention(input: string) {
  return parseChatProductCreationCommand(input) !== null
}

export function createActiveChatProductCreation(
  command: ChatProductCreationCommand
): ActiveChatProductCreation {
  const createdAt = Date.now()

  return {
    ...command,
    id: `chat-${command.kind}-${createdAt}`,
    createdAt,
    status: 'queued',
    updatedAt: createdAt
  }
}

export function getChatProductCreationKindFromToolName(
  toolName: string | null | undefined
): ChatProductCreationKind | null {
  if (!toolName) return null

  if (toolName === 'LosiProductCreation__createSite') {
    return 'sites'
  }

  if (toolName === 'LosiProductCreation__createEngine') {
    return 'engines'
  }

  return null
}

export function normalizeChatProductCreationToolResult(
  value: unknown
): ChatProductCreationToolResult | null {
  if (typeof value === 'string') {
    try {
      return normalizeChatProductCreationToolResult(JSON.parse(value))
    } catch {
      return null
    }
  }

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  const record = value as Record<string, unknown>
  const created = record.created

  if (
    record.superAgent !== 'Sites' &&
    record.superAgent !== 'Engines' &&
    created !== 'site' &&
    created !== 'engine_manifest'
  ) {
    return null
  }

  return record as unknown as ChatProductCreationToolResult
}

export function getSiteCreationHelpers(
  experienceId: LosiSiteExperienceId
): readonly ProductCreationHelper[] {
  if (experienceId === 'form') {
    return BASE_SITE_HELPERS.map((helper) =>
      helper.id === 'site-data'
        ? {
            ...helper,
            focus: 'Form fields, validation, submissions, routing, and privacy',
            outputs: ['Field model', 'Submission flow', 'Notification routing']
          }
        : helper
    )
  }

  if (experienceId === 'calculator') {
    return BASE_SITE_HELPERS.map((helper) =>
      helper.id === 'site-data'
        ? {
            ...helper,
            focus: 'Calculator inputs, formulas, assumptions, and result states',
            outputs: ['Input model', 'Formula notes', 'Result states']
          }
        : helper
    )
  }

  if (experienceId === 'quiz') {
    return BASE_SITE_HELPERS.map((helper) =>
      helper.id === 'site-data'
        ? {
            ...helper,
            focus: 'Quiz questions, scoring, outcomes, and follow-up routing',
            outputs: ['Question model', 'Scoring plan', 'Outcome mapping']
          }
        : helper
    )
  }

  return BASE_SITE_HELPERS
}

export function getEngineCreationHelpers(): readonly ProductCreationHelper[] {
  return ENGINE_HELPERS
}

export function buildSiteCreationName(
  experience: LosiSiteExperience,
  brief: string
) {
  const trimmedBrief = brief.replace(/\s+/g, ' ').trim()

  if (!trimmedBrief) {
    return `${experience.label} Draft`
  }

  const words = trimmedBrief.split(' ').slice(0, 8).join(' ')
  return words.replace(/\b\w/g, (character) => character.toUpperCase())
}

export function buildSiteCreationStructure(
  experience: LosiSiteExperience,
  brief: string
): SiteCreationStructure {
  const title = buildSiteCreationName(experience, brief)
  const description = brief.trim() || experience.description
  const formBlock =
    experience.id === 'form'
      ? [
          {
            id: 'form-1',
            type: 'form-placeholder' as const,
            eyebrow: 'Form',
            title: 'Start here',
            body: 'Initial form fields for the @Sites draft.',
            buttonLabel: 'Submit',
            fields: [
              { id: 'name', kind: 'text' as const, label: 'Name', required: true },
              { id: 'email', kind: 'email' as const, label: 'Email', required: true },
              { id: 'details', kind: 'textarea' as const, label: 'Details', required: false }
            ]
          }
        ]
      : []

  return {
    version: 1,
    siteType: experience.id,
    theme: {
      accentColor: '#2563eb',
      backgroundColor: '#ffffff',
      textColor: '#111827'
    },
    pages: [
      {
        path: '/',
        title,
        description,
        blocks: [
          {
            id: 'hero-1',
            type: 'hero',
            eyebrow: experience.label,
            title,
            body: description,
            buttonHref: '#next',
            buttonLabel: experience.id === 'form' ? 'Start form' : 'Start here'
          },
          {
            id: 'structure-1',
            type: 'feature-list',
            title: 'Suggested structure',
            body: 'A practical first draft from the @Sites brief.',
            items: [...experience.defaultSections]
          },
          {
            id: 'inputs-1',
            type: 'feature-list',
            title: 'Inputs and interactions',
            body: 'Data, controls, and interaction details to refine before publishing.',
            items: [...experience.inputs]
          },
          ...formBlock,
          {
            id: 'automation-1',
            type: 'feature-list',
            title: 'Automation hooks',
            body: 'Initial handoffs for the publishing and QA pass.',
            items: [...experience.automationHooks]
          },
          {
            id: 'cta-1',
            type: 'cta',
            title: 'Ready for the next step?',
            body: 'Review the draft, refine the specialist outputs, then publish when complete.',
            buttonHref: '#next',
            buttonLabel: 'Review draft'
          }
        ]
      }
    ]
  }
}

export function buildSiteCreationSettings(
  experience: LosiSiteExperience,
  brief: string
): SiteCreationSettings {
  return {
    source: 'chat-super-agent',
    superAgent: 'Sites',
    brief: brief.trim(),
    helperLanes: getSiteCreationHelpers(experience.id).map((helper) => ({
      id: helper.id,
      name: helper.name,
      focus: helper.focus,
      outputs: helper.outputs
    }))
  }
}

export function buildSiteCreationPrompt(
  experience: LosiSiteExperience,
  brief: string
) {
  const trimmedBrief = brief.trim()
  const userBrief = trimmedBrief || `Draft a ${experience.label.toLowerCase()} for my work.`

  return [
    experience.prompt,
    '',
    'Create this inside Losi Sites.',
    `Experience type: ${experience.label}`,
    experience.id === 'form' ? 'Forms are part of Sites.' : '',
    `User brief: ${userBrief}`,
    '',
    'Route this through @Sites with these specialist lanes:',
    helpersToPromptLines(getSiteCreationHelpers(experience.id)),
    '',
    'Required draft:',
    '- Structure and navigation or screen flow',
    '- Copy direction and content blocks',
    '- Fields, controls, states, or calculations where relevant',
    '- Knowledge needed from the workspace',
    '- Automation hooks and integration handoffs',
    '- Implementation checklist for the next build step'
  ]
    .filter(Boolean)
    .join('\n')
}

export function buildEngineCreationIntake(
  creation: Pick<ChatProductCreationCommand, 'prompt'>
): EngineCreationIntake {
  const prompt = creation.prompt.trim()
  const name = getEngineNameFromPrompt(prompt)
  const summary = prompt || 'Repeated operational workflow that may need its own dashboard UI, data model, workflow states, Helpers, tools, logs, settings, permissions, and approvals.'

  return {
    constraints: [
      'Internal Marshmallow Studio access only',
      'Only create an Engine if the repeated workflow needs a durable dashboard operating system',
      'Recommend Chat, Space, Site, Template, Automation, Nexus setup, or Helper workflow if the idea does not qualify',
      'Runtime activation requires explicit review',
      'Write actions require approval',
      'Execution must be split across Super Agent specialist lanes'
    ],
    domain: 'Repeated operational workflow',
    integrations: ['Workspace Knowledge', 'Automation Engine', 'Integrations'],
    knowledgeSources: ['Workspace Knowledge', 'Engine knowledge', 'Internal platform notes'],
    name,
    outcomes: [
      'Decide whether the workflow qualifies as an Engine',
      'Produce a reviewable Engine manifest and custom dashboard blueprint',
      'Map specialist lanes, permissions, dashboard sections, workflow stages, and data scopes',
      'Prepare internal approval gates before runtime activation'
    ],
    prompt,
    summary,
    targetUsers: ['Internal Marshmallow Studio operators'],
    tools: ['workflow_builder', 'manifest_review', 'schema_review', 'evaluation_review'],
    uiSurfaces: [
      'Overview',
      'Workflow Builder',
      'Records',
      'Helpers',
      'Tools',
      'Logs',
      'Approvals',
      'Settings'
    ]
  }
}

export function buildEngineReviewPrompt(
  creation: Pick<ChatProductCreationCommand, 'prompt'>,
  manifestName: string | null
) {
  const intake = buildEngineCreationIntake(creation)
  const name = manifestName || intake.name

  return [
    `Review the generated ${name} manifest inside Losi Engines.`,
    '',
    `Original brief: ${creation.prompt.trim() || intake.summary}`,
    '',
    'Route this through @Engines with these specialist lanes:',
    helpersToPromptLines(getEngineCreationHelpers()),
    '',
    'Check:',
    listToLines([
      'Whether this repeated workflow qualifies as an Engine',
      'If it does not qualify, recommend Chat, Space, Site, Template, Automation, Nexus setup, or Helper workflow',
      'Helper responsibilities and boundaries',
      'Data entities, sensitivity, retention, and audit coverage',
      'Tool permissions and integration assumptions',
      'Custom dashboard tabs, sections, workflow canvas, records, logs, settings, and permissions',
      'Evaluation and approval gates before activation'
    ])
  ].join('\n')
}
